import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { OrdersService } from '../../../services/orders.service';
import Statuses from '../../../models/statuses.json';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
  debounceTime,
  fromEvent,
  Subject,
  switchMap,
  tap,
  Subscription,
} from 'rxjs';
import { EventEmitterService } from '../../../services/event-emitter.service';
import { TeamMembersService } from '../../../services/team-members.service';
import { SocketService } from '../../../services/socket.service';
import { AuthenticationService } from '../../../services/authentication.service';
import { BranchesService } from '../../../services/branches.service';
import { TranslateService } from '@ngx-translate/core';
import { UsersService } from '../../../services/users.service';
import { Router } from '@angular/router';
import { DeletePopupComponent } from '../../../modals/delete-popup/delete-popup.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { AccountTypes } from '../../../models/account_types';

@Component({
  standalone: false,
  selector: 'app-orders',
  templateUrl: './orders.component.html',
  styleUrl: './orders.component.scss',
})
export class OrdersComponent implements OnInit, AfterViewInit, OnDestroy {
  specialId;
  @ViewChild('searchInput') searchInput: ElementRef;
  search = '';
  statuses: any = Statuses;
  riders1 = [];
  riders2 = [];
  searchRiders1 = new Subject<string>();
  searchRider1NoResults = 'no-results-found';
  searchRiders2 = new Subject<string>();
  searchRider2NoResults = 'no-results-found';
  selectedRider;
  isLoading = true;
  branches = [];
  selectedBranch;
  searchBranches = new Subject<string>();
  searchBranchNoResults = 'no-results-found';
  notificationSound = new Audio('../../../../assets/notification.mp3');
  isDeliveryBusiness = false;
  private socketSubscription: Subscription;
  private socketHealthCheckInterval: any;

  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private ordersService: OrdersService,
    public eventEmitterService: EventEmitterService,
    private teamMembersService: TeamMembersService,
    private branchesService: BranchesService,
    private socketService: SocketService,
    private translateService: TranslateService,
    private usersService: UsersService,
    private modalService: BsModalService,
  ) {
    this.eventEmitterService.invokeEvent.subscribe((event) => {
      if (event.action == 'order-deleted') {
        this.handleOrderDeleted(event.value);
      }
    });
  }

  ngOnInit() {
    this.getUser();
    this.getBusinessSpecialId();
    this.resetPagination();
    this.getAllRiders1('');
    this.getAllRiders2('');
    this.getAllBranches('');
    this.loadSelectedBranch();
    this.getAllOrders();
    this.getMineBusiness();
    this.setupSocketHealthCheck();
  }

  ngAfterViewInit(): void {
    this.setupSearch();
  }

  ngOnDestroy() {
    // Clean up socket subscription when component is destroyed
    if (this.socketSubscription) {
      this.socketSubscription.unsubscribe();
    }

    // Clean up health check interval
    if (this.socketHealthCheckInterval) {
      clearInterval(this.socketHealthCheckInterval);
    }

    // Disconnect socket when leaving orders route
    this.socketService.disconnect();
  }

  private setupSocketHealthCheck() {
    // Check socket connection every 30 seconds and attempt recovery if needed
    // Only run health check while on orders route
    this.socketHealthCheckInterval = setInterval(() => {
      if (this.specialId && !this.socketService.isConnected()) {
        console.log('Socket health check failed, attempting to reconnect...');
        this.socketService.reconnect();
        // Rejoin business room after reconnection
        setTimeout(() => {
          if (this.socketService.isConnected()) {
            this.socketService.joinBusinessRoom(this.specialId);
          }
        }, 1000);
      }
    }, 30000); // Check every 30 seconds
  }

  async getUser() {
    const user = await this.authenticationService.getUserFromToken();
    this.isDeliveryBusiness = user.user.business.type == AccountTypes.Delivery;
  }

  getMineBusiness() {
    this.usersService.getMineBusiness().subscribe(
      (response: any) => {
        let business = response;
        localStorage.setItem('businessName', business.name);
        localStorage.setItem(business.name + '-currency', business.currency);
      },
      (error) => {
        // if error.message is Business not found or Account not found logout and redirect to login
        if (
          error.message === 'Business not found' ||
          error.message === 'Account not found'
        ) {
          this.authenticationService.logout();
          this.router.navigate(['/login']);
        }
      },
    );
  }

  getBusinessSpecialId() {
    this.authenticationService.getBusinessSpecialId().then((specialId) => {
      this.specialId = specialId;
      console.log('Got business Special ID:', specialId);

      if (specialId) {
        // Ensure socket is connected when entering orders route
        if (!this.socketService.isConnected()) {
          console.log('Socket not connected, reconnecting...');
          this.socketService.reconnect();
        }

        console.log('Socket connected:', this.socketService.isConnected());

        // Join business room (this will work regardless of whether we just reconnected)
        console.log('Joining business room:', specialId);
        this.socketService.joinBusinessRoom(specialId);

        this.setupSocketSubscription();
      } else {
        console.warn('No business ID available');
        return;
      }
    });
  }

  private setupSocketSubscription() {
    // Unsubscribe from previous subscription if it exists
    if (this.socketSubscription) {
      this.socketSubscription.unsubscribe();
    }

    this.socketSubscription = this.socketService.getOrderUpdates().subscribe({
      next: (update) => {
        console.log('Received socket update:', update);
        if (update) {
          if (update.order && update.order.assignedTo) {
            update.order.assignedTo = {
              ...update.order.assignedTo,
              fullName:
                update.order.assignedTo.rider &&
                update.order.assignedTo.rider.name
                  ? update.order.assignedTo.rider.name
                  : '',
            };
          }
          switch (update.type) {
            case 'NEW':
              this.handleNewOrder(update.order);
              break;
            case 'UPDATE':
              this.handleOrderUpdate(update.order);
              break;
            case 'ASSIGNED':
              this.handleOrderAssignment(update.order);
              break;
            case 'DELETED':
              this.handleOrderDeleted(update.order);
              break;
          }
          this.sortOrders();
        }
      },
      error: (error) => {
        console.error('Socket update error:', error);
      },
    });
  }

  sortOrders() {
    this.statuses.forEach((status) => {
      if (status.orders && status.orders.length > 0) {
        status.orders.sort((a, b) => {
          // First sort by shipping_date (ASC)
          const shippingDateA = a.shipping_date
            ? new Date(a.shipping_date).getTime()
            : 0;
          const shippingDateB = b.shipping_date
            ? new Date(b.shipping_date).getTime()
            : 0;

          if (shippingDateA !== shippingDateB) {
            return shippingDateA - shippingDateB;
          }

          // Then sort by created_at (ASC)
          const createdAtA = new Date(a.created_at).getTime();
          const createdAtB = new Date(b.created_at).getTime();

          if (createdAtA !== createdAtB) {
            return createdAtA - createdAtB;
          }

          // Finally sort by id (ASC)
          return a.id - b.id;
        });
      }
    });
  }

  resetPagination() {
    this.statuses.forEach((status) => {
      status.orders = [];
      status.page = 1;
      status.pageSize = 10;
      status.totalItems = 0;
      status.totalPages = 1;
    });
  }

  getAllRiders1(search) {
    const apiData: any = {};
    if (search && search.trim().length > 0) {
      apiData.search = search;
    }
    this.teamMembersService.getAllRiders(apiData).subscribe(
      (response: any) => {
        console.log('response:');
        console.log(response);
        this.searchRider1NoResults = 'no-results-found';
        this.riders1 = response.items;
        this.riders1 = this.riders1.map((item) => ({
          ...item,
          fullName: item.rider.name,
        }));
        let noneString = this.translateService.instant('none');
        this.riders1.unshift({ id: 0, fullName: noneString });
      },
      (error) => {
        console.log('error:');
        console.log(error);
        this.searchRider1NoResults = 'no-results-found';
      },
    );
  }

  getAllRiders2(search) {
    const apiData: any = {};
    if (search && search.trim().length > 0) {
      apiData.search = search;
    }
    this.teamMembersService.getAllRiders(apiData).subscribe(
      (response: any) => {
        console.log('response:');
        console.log(response);
        this.searchRider2NoResults = 'no-results-found';
        this.riders2 = response.items;
        this.riders2 = this.riders2.map((item) => ({
          ...item,
          fullName: item.rider.name,
        }));
        let noneString = this.translateService.instant('none');
        this.riders2.unshift({ id: 0, fullName: noneString });
      },
      (error) => {
        console.log('error:');
        console.log(error);
        this.searchRider2NoResults = 'no-results-found';
      },
    );
  }

  getAllOrders() {
    const apiData: any = { statuses: [] };
    this.statuses.forEach((status) => {
      if (!status.loading && status.page <= status.totalPages) {
        status.loading = true;
        apiData.statuses.push({
          id: status.id,
          page: status.page,
          pageSize: status.pageSize,
        });
      }
    });
    if (apiData.statuses.length <= 0) {
      return;
    }
    if (this.selectedRider) {
      apiData.assigned_to = this.selectedRider.id;
    }
    if (this.selectedBranch) {
      apiData.branch_id = this.selectedBranch.id;
    }
    if (this.search && this.search.trim().length > 0) {
      apiData.search = this.search;
    }
    this.isLoading = true;
    this.ordersService.getAllOrders(apiData).subscribe(
      (response: any) => {
        this.isLoading = false;
        this.statuses.forEach((status1) => {
          apiData.statuses.forEach((status2) => {
            if (status1.id == status2.id) {
              status1.loading = false;
            }
          });
        });

        this.statuses.forEach((status1) => {
          response.statuses.forEach((status2) => {
            if (status1.id == status2.id) {
              status1.orders = [...status1.orders, ...status2.items];
              status1.page = status2.page + 1;
              status1.pageSize = status2.pageSize;
              status1.totalItems = status2.totalItems;
              status1.totalPages = status2.totalPages;

              status1.orders = status1.orders.map((item) => ({
                ...item,
                timeAgoString: this.getOrderTimeAgoString(item),
                assignedTo: {
                  ...item.assignedTo,
                  fullName: item.assignedTo.rider
                    ? item.assignedTo.rider.name
                    : '',
                },
              }));
              console.log('status1.orders:');
              console.log(status1.orders);
            }
          });
        });
      },
      (error) => {
        this.isLoading = false;
        console.log('error:');
        console.log(error);
        this.statuses.forEach((status1) => {
          apiData.statuses.forEach((status2) => {
            if (status1.id == status2.id) {
              status1.loading = false;
            }
          });
        });
      },
    );
  }

  getOrderTimeAgoString(order) {
    const now = new Date();
    const orderDate = new Date(order.created_at);
    const diffTime = Math.abs(now.getTime() - orderDate.getTime());

    // Convert to different time units using Math.floor for accurate results
    const diffSeconds = Math.floor(diffTime / 1000);
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    // Return the appropriate time unit with early returns
    if (diffYears > 0) {
      return (
        diffYears +
        (diffYears === 1
          ? this.translateService.instant('year-ago')
          : this.translateService.instant('years-ago'))
      );
    }
    if (diffMonths > 0) {
      return (
        diffMonths +
        (diffMonths === 1
          ? this.translateService.instant('month-ago')
          : this.translateService.instant('months-ago'))
      );
    }
    if (diffWeeks > 0) {
      return (
        diffWeeks +
        (diffWeeks === 1
          ? this.translateService.instant('week-ago')
          : this.translateService.instant('weeks-ago'))
      );
    }
    if (diffDays > 0) {
      return (
        diffDays +
        (diffDays === 1
          ? this.translateService.instant('day-ago')
          : this.translateService.instant('days-ago'))
      );
    }
    if (diffHours > 0) {
      return (
        diffHours +
        (diffHours === 1
          ? this.translateService.instant('hour-ago')
          : this.translateService.instant('hours-ago'))
      );
    }
    if (diffMinutes > 0) {
      return (
        diffMinutes +
        (diffMinutes === 1
          ? this.translateService.instant('minute-ago')
          : this.translateService.instant('minutes-ago'))
      );
    }
    if (diffSeconds > 0) {
      return (
        diffSeconds +
        (diffSeconds === 1
          ? this.translateService.instant('second-ago')
          : this.translateService.instant('seconds-ago'))
      );
    }

    return this.translateService.instant('just-now');
  }

  getAllBranches(search) {
    const apiData: any = {
      page: 1,
      pageSize: 10,
    };

    if (search && search.trim().length > 0) {
      apiData.search = this.search;
    }
    this.branchesService.getAllParentBranches(apiData).subscribe(
      (response: any) => {
        this.branches = response.items;
      },
      (error) => {
        console.log('error:');
        console.log(error);
      },
    );
  }

  loadSelectedBranch() {
    const selectedBranch = localStorage.getItem('selectedBranch');
    if (selectedBranch) {
      this.selectedBranch = JSON.parse(selectedBranch);
    }
  }

  @HostListener('scroll', ['$event'])
  onScroll(event: any) {
    const bottom =
      event.target.scrollHeight ===
      event.target.scrollTop + event.target.clientHeight;
    if (bottom) {
      this.getAllOrders();
    }
  }

  setupSearch() {
    fromEvent(this.searchInput.nativeElement, 'input')
      .pipe(
        tap(() => (this.isLoading = true)),
        debounceTime(3000),
      )
      .subscribe((event: Event) => {
        this.resetPagination();
        this.getAllOrders();
      });

    this.searchRiders1
      .pipe(
        tap(() => (this.searchRider1NoResults = 'loading')),
        debounceTime(3000),
        switchMap(async (searchText) => this.getAllRiders1(searchText)),
      )
      .subscribe();

    this.searchRiders2
      .pipe(
        tap(() => (this.searchRider2NoResults = 'loading')),
        debounceTime(3000),
        switchMap(async (searchText) => this.getAllRiders2(searchText)),
      )
      .subscribe();

    this.searchBranches
      .pipe(
        tap(() => (this.searchBranchNoResults = 'loading')),
        debounceTime(3000),
        switchMap(async (searchText) => this.getAllBranches(searchText)),
      )
      .subscribe();
  }

  setSearch(event: any) {
    this.search = event.target.value;
  }

  setAssignedTo(event: any) {
    this.selectedRider = event.value;
    this.resetPagination();
    this.getAllOrders();
  }

  setOrderAssignedTo(order, event: any) {
    let assigned_to = 0;
    if (event.value) {
      assigned_to = event.value.id;
    }
    this.ordersService
      .setOrderAssignedTo(order.id, assigned_to)
      .subscribe((response) => {
        order = { ...order, ...response };
      });
  }

  moveOrder(order, direction) {
    console.log('order:');
    console.log(order);
    console.log('direction:');
    console.log(direction);

    if (direction == 'left' && order.status == this.statuses[0].id) {
      return;
    }
    if (
      direction == 'right' &&
      order.status == this.statuses[this.statuses.length - 1].id
    ) {
      return;
    }

    if (direction == 'left') {
      order.status = order.status - 1;
    } else if (direction == 'right') {
      order.status = order.status + 1;
    }
    this.ordersService
      .setOrderStatus(order.id, order.status)
      .subscribe((response) => {
        order = { ...order, ...response };
      });
  }

  onDrop(event: CdkDragDrop<any[]>) {
    const previousContainerData = event.previousContainer.data;
    const currentContainerData = event.container.data;

    console.log('event.previousContainer.id:');
    console.log(event.previousContainer.id);
    console.log('event.container.id:');
    console.log(event.container.id);

    let order = previousContainerData[event.previousIndex];

    order.status = event.container.id;

    if (event.previousContainer == event.container) {
      currentContainerData.splice(event.previousIndex, 1);
      currentContainerData.splice(event.currentIndex, 0, order);
    } else {
      previousContainerData.splice(event.previousIndex, 1);
      currentContainerData.push(order);

      this.ordersService
        .setOrderStatus(order.id, order.status)
        .subscribe((response) => {
          order = { ...order, ...response };
        });
    }
  }

  private handleNewOrder(newOrder: any) {
    // Add the new order to the appropriate status column
    const status = this.statuses.find((s) => s.id === newOrder.status);
    if (status) {
      newOrder.timeAgoString = this.getOrderTimeAgoString(newOrder);
      status.orders.push(newOrder); // Add to the bottom of the list
    }
    // make a notification sound
    this.notificationSound.play();
  }

  private handleOrderUpdate(updatedOrder: any) {
    // Update the order in the appropriate status column
    this.statuses.forEach((status) => {
      const orderIndex = status.orders.findIndex(
        (o) => o.id === updatedOrder.id,
      );
      if (orderIndex !== -1) {
        if (status.id === updatedOrder.status) {
          // Update the order in its current status
          status.orders[orderIndex] = {
            ...status.orders[orderIndex],
            ...updatedOrder,
          };
        } else {
          // Remove from current status and add to new status
          status.orders.splice(orderIndex, 1);
          const newStatus = this.statuses.find(
            (s) => s.id === updatedOrder.status,
          );
          if (newStatus) {
            updatedOrder.timeAgoString =
              this.getOrderTimeAgoString(updatedOrder);
            newStatus.orders.push(updatedOrder);
          }
        }
      }
    });
  }

  private handleOrderAssignment(assignedOrder: any) {
    // Similar to handleOrderUpdate but specific to assignment changes
    this.handleOrderUpdate(assignedOrder);
  }

  private handleOrderDeleted(deletedOrder: any) {
    // Remove order from the UI
    const status = this.statuses.find((s) => s.id === deletedOrder.status);
    if (status) {
      const orderIndex = status.orders.findIndex(
        (o) => o.id === deletedOrder.id,
      );
      if (orderIndex !== -1) {
        status.orders.splice(orderIndex, 1);
      }
    }
  }

  setBranch(event: any) {
    this.selectedBranch = event.value;
    localStorage.setItem('selectedBranch', JSON.stringify(this.selectedBranch));
    this.resetPagination();
    this.getAllOrders();
  }

  deleteOrderPopup(order) {
    this.modalService.show(DeletePopupComponent, {
      initialState: {
        title: 'delete-order',
        extra: '#' + order.special_id,
        text: 'delete-order-text',
        id: order.id,
        onButtonClick: this.deleteOrder.bind(this),
      },
      class: 'alert-modal modal-dialog-centered',
      backdrop: 'static',
    });
  }

  printOrderQrCode(event: MouseEvent, order) {
    // reuse the qr code already rendered (hidden) inside this order item
    const orderItem = (event.target as HTMLElement).closest('.order-item');
    const canvas = orderItem?.querySelector('qrcode canvas') as HTMLCanvasElement;
    if (!canvas) {
      return;
    }

    const printWindow = window.open('', '_blank', 'width=400,height=600');
    if (!printWindow) {
      return;
    }

    const escape = (value) =>
      String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const details = [
      order.business && order.branch
        ? `${order.business.name} - ${order.branch.name}`
        : '',
      order.fullname,
      order.phone,
      order.location,
    ]
      .filter((detail) => detail)
      .map((detail) => `<p>${escape(detail)}</p>`)
      .join('');

    printWindow.document.write(`
      <html>
        <head>
          <title>${escape(order.special_id)}</title>
          <style>
            body { font-family: Poppins, Arial, sans-serif; text-align: center; margin: 0; padding: 16px; }
            img { width: 260px; height: 260px; image-rendering: pixelated; }
            h1 { font-size: 22px; margin: 8px 0; letter-spacing: 1px; }
            p { font-size: 14px; margin: 4px 0; }
          </style>
        </head>
        <body>
          <img src="${canvas.toDataURL('image/png')}" />
          <h1>#${escape(order.special_id)}</h1>
          ${details}
        </body>
      </html>
    `);
    printWindow.document.close();

    const image = printWindow.document.querySelector('img');
    const print = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
    if (image.complete) {
      print();
    } else {
      image.onload = print;
    }
  }

  async deleteOrder(id): Promise<void> {
    console.log('delete order id:');
    console.log(id);
    return new Promise((resolve, reject) => {
      this.ordersService.deleteOrder(id).subscribe(
        (response: any) => {
          console.log('response:');
          console.log(response);
          this.eventEmitterService.callOrderDeleted(response.result);
          resolve();
        },
        (error) => {
          console.log('error:');
          console.log(error);
          reject();
        },
      );
    });
  }
}
